import dayjs, { Dayjs } from 'dayjs';
import { isHourlyPricingValue } from '../General/hourlyPricingFormUtils';

export type UnavailabilityDateRange = {
  id?: number;
  unavailabilityStartDate: string;
  unavailabilityEndDate: string;
};

const API_DATE_FORMATS = ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'] as const;

export const parseUnavailabilityDate = (value?: string | null): Dayjs | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return dayjs(value);
  }
  const parsed = dayjs(value, [...API_DATE_FORMATS], true);
  if (parsed.isValid()) return parsed;
  const fallback = dayjs(value);
  return fallback.isValid() ? fallback : null;
};

export const formatUnavailabilityDisplayDate = (value?: string): string => {
  const parsed = parseUnavailabilityDate(value);
  return parsed ? parsed.format('DD MMM YYYY') : value ?? '';
};

export const toApiUnavailabilityDate = (value: string): string => {
  const parsed = parseUnavailabilityDate(value);
  return parsed ? parsed.format('DD-MM-YYYY') : value;
};

export const mapUnavailabilityDatesFromApi = (items: any[] = []): UnavailabilityDateRange[] =>
  items.map(item => ({
    id: item.id,
    unavailabilityStartDate: item.unavailabilityStartDate ?? '',
    unavailabilityEndDate: item.unavailabilityEndDate ?? '',
  }));

export const buildUnavailabilityPayload = (dates: UnavailabilityDateRange[]) =>
  dates.map(date => ({
    ...(date.id != null ? { id: date.id } : {}),
    unavailabilityStartDate: toApiUnavailabilityDate(date.unavailabilityStartDate),
    unavailabilityEndDate: toApiUnavailabilityDate(date.unavailabilityEndDate),
  }));

export const getHighlightedDaysForMonth = (dates: UnavailabilityDateRange[], month: Dayjs): number[] => {
  const days = new Set<number>();
  const monthStart = month.startOf('month');
  const monthEnd = month.endOf('month');

  for (const range of dates) {
    const start = parseUnavailabilityDate(range.unavailabilityStartDate);
    const end = parseUnavailabilityDate(range.unavailabilityEndDate);
    if (!start || !end || end.isBefore(start, 'day')) continue;
    if (end.isBefore(monthStart, 'day') || start.isAfter(monthEnd, 'day')) continue;

    let cursor = start.isBefore(monthStart, 'day') ? monthStart : start;
    const rangeEnd = end.isAfter(monthEnd, 'day') ? monthEnd : end;

    while (!cursor.isAfter(rangeEnd, 'day')) {
      days.add(cursor.date());
      cursor = cursor.add(1, 'day');
    }
  }

  return Array.from(days);
};

export const buildGeneralRequestFromPageResponse = (
  pageResponse: any,
  unavailabilityDates: UnavailabilityDateRange[],
) => {
  const isHourly = isHourlyPricingValue(pageResponse?.hourlyPricing);
  return {
    venueId: pageResponse?.venues?.value ?? pageResponse?.venues?.id,
    available: pageResponse.available?.value ?? pageResponse.available ?? false,
    onlyRFQ: pageResponse.onlyRFQ?.value ?? pageResponse.onlyRFQ ?? false,
    eventTypeId: pageResponse.eventTypeId?.value ?? pageResponse.eventTypeId,
    tentativeBookingAllowed:
      pageResponse.tentativeBookingAllowed?.value ?? pageResponse.tentativeBookingAllowed ?? false,
    maxBookingCapacity: pageResponse.maxBookingCapacity,
    minimumRevenue: isHourly ? null : pageResponse.minimumRevenue,
    minimumDaysBeforeBooking: pageResponse.minimumDaysBeforeBooking,
    fixedHallRental: isHourly ? null : pageResponse.fixedHallRental || null,
    guestCountForFixedHallRental: pageResponse.guestCountForFixedHallRental || null,
    holdDays: pageResponse.holdDays || null,
    preLeadTime: pageResponse.preLeadTime || 0,
    postLeadTime: pageResponse.postLeadTime || 0,
    hourlyPricing: isHourly,
    pricePerHour: isHourly
      ? pageResponse.pricePerHour != null && pageResponse.pricePerHour !== ''
        ? parseFloat(String(pageResponse.pricePerHour))
        : null
      : null,
    unavailabilityDates: buildUnavailabilityPayload(unavailabilityDates),
  };
};

export const unavailabilityToFormValues = (range: UnavailabilityDateRange) => {
  const start = parseUnavailabilityDate(range.unavailabilityStartDate);
  const end = parseUnavailabilityDate(range.unavailabilityEndDate);
  return {
    unavailabilityStartDate: start ? start.valueOf() : '',
    unavailabilityEndDate: end ? end.valueOf() : '',
  };
};
